import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { pairwise, startWith, Subject, takeUntil } from 'rxjs';
import { IFormKey, IFormSelectOption } from 'src/app/modules/shared/models/forms';
import { LoggerService } from 'src/app/services/logger.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { AthoraService } from '../../athora.service';
import { CODICE_NAZIONE_ITALIA, SIGLA_CAP_STATO_ESTERO, SIGLA_CITTA_STATO_ESTERO, SIGLA_PROVINCIA_STATO_ESTERO } from '../contractor-variation/contractor-variation.constants';
import { address_change_enabling_map, address_change_form_keys, address_change_form_validators, hidden_field_foreign_state } from './address-change.constants';

@Component({
  selector: 'app-address-change',
  templateUrl: './address-change.component.html',
  styleUrl: './address-change.component.scss'
})
export class AddressChangeComponent implements OnInit, OnDestroy {

  @Input() form: FormGroup;
  @Input() isReadOnly: boolean;
  @Input() countriesList: IFormSelectOption[];
  @Input() provincesList: IFormSelectOption[];
  @Input() citiesList: IFormSelectOption[];
  formKeys: IFormKey[] = CommonUtils.assign(address_change_form_keys);
  destroy$ = new Subject();

  constructor(
    private readonly _athoraService: AthoraService
  ) { }

  ngOnInit(): void {
    this.watchForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countriesList'] && changes['countriesList'].currentValue) {
      const countriesKey = this.formKeys.find(key => key.controlName === 'country');
      if (countriesKey) {
        countriesKey.options = changes['countriesList'].currentValue;
      }
    }
    if (changes['provincesList'] && changes['provincesList'].currentValue) {
      const provincesKey = this.formKeys.find(key => key.controlName === 'province');
      if (provincesKey) {
        provincesKey.type = 'SELECT';
        provincesKey.options = changes['provincesList'].currentValue;
      }
    }
    if (changes['citiesList'] && changes['citiesList'].currentValue) {
      const citiesKey = this.formKeys.find(key => key.controlName === 'place');
      if (citiesKey) {
        citiesKey.type = 'SELECT';
        citiesKey.options = changes['citiesList'].currentValue;
      }
    }
  }

  watchForm(): void {
    Object.keys(address_change_enabling_map).forEach(controlName => {
      this.form.get(controlName).valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          if (!this.isForeignState && !this.form.disabled) {
            const childControlName = address_change_enabling_map[controlName];
            const control = this.form.get(childControlName);
            if (value && this.form.get(controlName)) {
              control.enable();
              control.reset(/* { emitEvent: false } */);
              control.setValidators(address_change_form_validators[childControlName]);
            } else {
              control.reset(/* { emitEvent: false } */);
              control.disable();
            }
          }
        })
    })

    this.form.get('country').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        pairwise()
      )
      .subscribe(([prev, next]) => {
        console.warn('Country changed from', prev, 'to', next);
        if(prev !== next) {
          if (next && !this.form.disabled) {
            if (this.isForeignState) {
              this.manageForeignState();
            } else {
              this._athoraService.retrieveProvicesList();
              this.form.get('toponym').setValidators(address_change_form_validators['toponym']);
            }
          }
        }
      })

    this.form.get('province').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && !this.isForeignState && !this.form.disabled) {
          this._athoraService.retrieveCitiesList(value);
        }
      })
  }

  manageForeignState(): void {
    this.formKeys.forEach(key => {
      if (hidden_field_foreign_state.includes(key.controlName)) {
        this.form.get(key.controlName).reset();
        this.form.get(key.controlName).disable();
        this.form.get(key.controlName).setValidators(null);
        if (key.controlName === 'place') {
          key.type = 'INPUT';
          this.form.get(key.controlName).patchValue(SIGLA_CITTA_STATO_ESTERO);
        } else if (key.controlName === 'province') {
          key.type = 'INPUT';
          this.form.get(key.controlName).patchValue(SIGLA_PROVINCIA_STATO_ESTERO);
        } else if (key.controlName === 'postalCode') {
          this.form.get(key.controlName).patchValue(SIGLA_CAP_STATO_ESTERO);
        }
      } else if (key.controlName !== 'country') {
        this.form.get(key.controlName).reset();
        this.form.get(key.controlName).enable();
        if (key.controlName === 'toponym') {
          this.form.get('toponym').setValidators(null);
        }
      }
    });
  }

  reset(): void {
    LoggerService.logInfo(this.constructor.name, 'reset');
    if (this.formKeys) {
      this.formKeys.forEach(key => {
        const control = this.form.get(key.controlName);
        if (control) {
          control.setValue(null);
          if (key.controlName !== 'country') {
            control.disable();
          }
        }
      })
    }
  }

  get isForeignState(): boolean {
    return this.form && this.form.get('country').value !== CODICE_NAZIONE_ITALIA;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
